/**
 * Routes whose PATH carries workspace-scoped state.
 *
 * A project, an application and a build each belong to exactly one workspace,
 * but the routes that name them carry no workspace of their own. That is what
 * made switching workspace on /build appear to do nothing: the URL still
 * asserted a selection from the workspace the user had just left, and the
 * session-restore waterfall ("URL deep-link wins") put it straight back.
 *
 * Switching workspace therefore has to LEAVE these routes, not merely try to
 * correct what they restore.
 */

/** Pattern → the nearest ancestor route that is workspace-agnostic. */
const WORKSPACE_SCOPED_ROUTES: ReadonlyArray<{ match: RegExp; base: string }> = [
  // /build/:projectId[/:applicationId[/:buildId]]
  { match: /^\/build\/[^/]+/, base: '/build' },
  // /projects/:projectId/settings
  { match: /^\/projects\/[^/]+\/settings/, base: '/projects' },
  // /deployments/:projectId
  { match: /^\/deployments\/[^/]+/, base: '/deployments' },
  // /apps/:appId/{previews,logs,metrics} — there is no bare /apps route, so the
  // honest landing place is the deployments list these are reached from.
  { match: /^\/apps\/[^/]+\//, base: '/deployments' },
]

/**
 * The workspace-agnostic route to fall back to, or null if `pathname` is
 * already safe to stay on.
 *
 * Returning null for an already-bare route matters: navigating anyway would
 * push a redundant history entry and remount the page on every switch.
 */
export function workspaceScopedBase(pathname: string): string | null {
  const path = pathname.split('?')[0].split('#')[0]
  for (const route of WORKSPACE_SCOPED_ROUTES) {
    if (route.match.test(path)) return route.base
  }
  return null
}


/**
 * Build the /build route path from the active context.
 *
 * The route is NESTED — /build/:projectId/:applicationId/:buildId — so a child
 * id is meaningless without its parent, and each segment must keep its
 * position. This used to be `[pid, aid, bid].filter(Boolean).join('/')`, which
 * collapsed them: with no project but an application still selected it emitted
 * `/build/<applicationId>`, and the next load parsed that application id as a
 * PROJECT id. `GET /api/projects/<applicationId>` 404s, so the Project panel
 * sat on its "Loading..." stub indefinitely — seen in prod 2026-09-15 right
 * after a workspace switch, once the URL sync began running for the
 * no-project case so that a switch could clear the deep link.
 */
export function buildRoutePath(
  projectId?: string | null,
  applicationId?: string | null,
  buildId?: string | null,
): string {
  if (!projectId) return '/build'
  const parts = [projectId]
  if (applicationId) {
    parts.push(applicationId)
    if (buildId) parts.push(buildId)
  }
  return `/build/${parts.join('/')}`
}
